/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: tabs\RecordingTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Extensions module.
----------------------------------------------------------------*/

import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Toggle } from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { FormBox } from '../../../components/ui/FormBox';
import { useI18n } from '../../../hooks/useI18n';
import { useDebugMode } from '../../../hooks/useDebugMode';
import { Mic } from 'lucide-react';

interface RecordingTabProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  recordingSettings: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (field: string, value: any) => void;
}

export function RecordingTab({ recordingSettings, onChange }: RecordingTabProps) {
  const { t } = useI18n();
  const { isDebugModeActive } = useDebugMode();

  return (
    <div className="space-y-4">
      <FormBox
        title={t('extensions.recording.title')}
        icon={<Mic className="h-5 w-5" />}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-start">
          {/* Record Incoming */}
          <SimpleTooltip
            content={isDebugModeActive ? `${t('extensions.recording.recordIncoming.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.record_incoming')}` : t('extensions.recording.recordIncoming.tooltip')}
            title={t('extensions.recording.recordIncoming.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="recordIncoming" className="text-left">
              {t('extensions.recording.recordIncoming.label')}
            </Label>
          </SimpleTooltip>
          <Toggle
            id="recordIncoming"
            value={recordingSettings.recordIncoming}
            onChange={(value) => onChange('recordIncoming', value)}
          />

          {/* Record Outgoing */}
          <SimpleTooltip
            content={isDebugModeActive ? `${t('extensions.recording.recordOutgoing.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.record_outgoing')}` : t('extensions.recording.recordOutgoing.tooltip')}
            title={t('extensions.recording.recordOutgoing.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="recordOutgoing" className="text-left">
              {t('extensions.recording.recordOutgoing.label')}
            </Label>
          </SimpleTooltip>
          <Toggle
            id="recordOutgoing"
            value={recordingSettings.recordOutgoing}
            onChange={(value) => onChange('recordOutgoing', value)}
          />

          {/* Record Internal */}
          <SimpleTooltip
            content={isDebugModeActive ? `${t('extensions.recording.recordInternal.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.record_internal')}` : t('extensions.recording.recordInternal.tooltip')}
            title={t('extensions.recording.recordInternal.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="recordInternal" className="text-left">
              {t('extensions.recording.recordInternal.label')}
            </Label>
          </SimpleTooltip>
          <Toggle
            id="recordInternal"
            value={recordingSettings.recordInternal}
            onChange={(value) => onChange('recordInternal', value)}
          />

          {/* Record On Demand */}
          <SimpleTooltip
            content={isDebugModeActive ? `${t('extensions.recording.recordOnDemand.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.record_on_demand')}` : t('extensions.recording.recordOnDemand.tooltip')}
            title={t('extensions.recording.recordOnDemand.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="recordOnDemand" className="text-left">
              {t('extensions.recording.recordOnDemand.label')}
            </Label>
          </SimpleTooltip>
          <Toggle
            id="recordOnDemand"
            value={recordingSettings.recordOnDemand}
            onChange={(value) => onChange('recordOnDemand', value)}
          />

          {/* Recording Format */}
          <SimpleTooltip
            content={isDebugModeActive ? `${t('extensions.recording.recordingFormat.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.recording_format')}` : t('extensions.recording.recordingFormat.tooltip')}
            title={t('extensions.recording.recordingFormat.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="recordingFormat" className="text-left">
              {t('extensions.recording.recordingFormat.label')}
            </Label>
          </SimpleTooltip>
          <select
            id="recordingFormat"
            value={recordingSettings.recordingFormat}
            onChange={(e) => onChange('recordingFormat', e.target.value)}
            className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="wav">WAV</option>
            <option value="mp3">MP3</option>
            <option value="ogg">OGG</option>
          </select>

          {/* Record Path */}
          <SimpleTooltip
            content={isDebugModeActive ? `${t('extensions.recording.recordPath.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.record_path')}` : t('extensions.recording.recordPath.tooltip')}
            title={t('extensions.recording.recordPath.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="recordPath" className="text-left">
              {t('extensions.recording.recordPath.label')}
            </Label>
          </SimpleTooltip>
          <Input
            id="recordPath"
            value={recordingSettings.recordPath}
            onChange={(e) => onChange('recordPath', e.target.value)}
            placeholder={t('extensions.recording.recordPath.placeholder')}
          />

          {/* Record Template */}
          <SimpleTooltip
            content={isDebugModeActive ? `${t('extensions.recording.recordTemplate.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.recording_filename_template')}` : t('extensions.recording.recordTemplate.tooltip')}
            title={t('extensions.recording.recordTemplate.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="recordTemplate" className="text-left">
              {t('extensions.recording.recordTemplate.label')}
            </Label>
          </SimpleTooltip>
          <Input
            id="recordTemplate"
            value={recordingSettings.recordTemplate}
            onChange={(e) => onChange('recordTemplate', e.target.value)}
            placeholder={t('extensions.recording.recordTemplate.placeholder')}
          />

          {/* Empty cells to complete the row */}
          <div className="col-span-2"></div>
        </div>
      </FormBox>
    </div>
  );
}
